import { yupResolver } from '@hookform/resolvers/yup';
import { styled, TableBody, TableCell, TableContainer } from '@material-ui/core';
import { Button, Table, TableHead, TableRow } from '@mui/material';
import { Modal, SGSForm, SGSFormInput, SGSFormLayout } from 'components';
import SGSTablePagination from 'components/SGSTablePagination';
import { useArretTravailFormContext } from 'features/arretTravail/contexts/ArretTravailFormProvider';
import { getEtablissements } from 'features/arretTravail/services/workStoppageService';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';

type Props = {
    handleEtablissementSelection: Function;
    siret: string;
};

type SearchFormSchema = {
    name: string;
    siret: string;
};

const ModalAjouterEtablissement: React.FC<Props> = ({ handleEtablissementSelection, siret }) => {
    const { isModalAddEts, setIsModalAddEts } = useArretTravailFormContext();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [searchParams, setSearchParams] = useState<SearchFormSchema>({
        name: '',
        siret: '',
    });

    const [establishments, setEstablishments] = useState<any[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const validationSchema = Yup.object().shape({
        name: Yup.string().optional(),
        siret: Yup.string().optional(),
    });

    const methods = useForm<SearchFormSchema>({
        resolver: yupResolver(validationSchema),
        defaultValues: searchParams,
    });

    const { handleSubmit, reset } = methods;

    // Fetch data directly using getEtablissements
    const fetchEtablissements = async () => {
        if (!siret) return;
        setIsLoading(true);
        try {
            const sortPayload = JSON.stringify({ column: 'name', order: 'ASC' });
            const data = await getEtablissements(page + 1, rowsPerPage, sortPayload, searchParams, siret);
            setEstablishments(data.data || []);
            setTotalItems(data.totalResultat || 0);
        } catch (error) {
            console.error('Error fetching establishments:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch data when modal opens
    useEffect(() => {
        if (isModalAddEts) {
            fetchEtablissements();
        }
    }, [isModalAddEts]);

    // Fetch data on pagination or search params change
    useEffect(() => {
        if (isModalAddEts) {
            fetchEtablissements();
        }
    }, [page, rowsPerPage, searchParams]);

    const handleSearchSubmit = (data: SearchFormSchema) => {
        setSearchParams({ ...data });
        setPage(0);
    };

    const handleCancel = () => {
        reset();
        setSearchParams({ name: '', siret: '' });
        setPage(0);
    };

    const handleSelection = (establishment: any) => {
        handleEtablissementSelection(establishment);
        console.log('Selected establishment:', establishment);
        setIsModalAddEts(false);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Modal setIsOpenModal={() => setIsModalAddEts(false)} title={"Sélectionner établissement d'attache"} isOpenModal={isModalAddEts}>
            <FormProvider {...methods}>
                <SGSFormLayout onSubmit={handleSubmit(handleSearchSubmit)}>
                    <SGSForm.Row>
                        <SGSForm.Col md={6}>
                            <SGSFormInput variant="outlined" name="name" label="Raison sociale" />
                        </SGSForm.Col>
                        <SGSForm.Col md={6}>
                            <SGSFormInput variant="outlined" name="siret" label="Siret" />
                        </SGSForm.Col>
                    </SGSForm.Row>
                    <SGSForm.Row>
                        <SGSForm.Col md={6}></SGSForm.Col>
                        <SGSForm.Col md={3}>
                            <Button type="button" variant="outlined" color="secondary" onClick={handleCancel}>
                                Annuler
                            </Button>
                        </SGSForm.Col>
                        <SGSForm.Col md={3}>
                            <Button type="submit" variant="contained" color="primary">
                                Rechercher
                            </Button>
                        </SGSForm.Col>
                    </SGSForm.Row>
                </SGSFormLayout>
            </FormProvider>
            <StyledTableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <StyledHeaderCell>SIRET</StyledHeaderCell>
                            <StyledHeaderCell>Raison Sociale</StyledHeaderCell>
                            <StyledHeaderCell>Site / Magasin / ...</StyledHeaderCell>
                            <StyledHeaderCell>Action</StyledHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <StyledTableCell colSpan={4} style={{ textAlign: 'center' }}>
                                    Chargement...
                                </StyledTableCell>
                            </TableRow>
                        ) : establishments.length > 0 ? (
                            establishments.map((establishment: any, index: number) => (
                                <TableRow key={index} hover onClick={() => handleSelection(establishment)} style={{ cursor: 'pointer' }}>
                                    <StyledTableCell>{establishment.siret}</StyledTableCell>
                                    <StyledTableCell>{establishment.name}</StyledTableCell>
                                    <StyledTableCell>{establishment.site}</StyledTableCell>
                                    <StyledTableCell>
                                        <Button variant="outlined" color="primary" onClick={() => handleSelection(establishment)}>
                                            Sélectionner
                                        </Button>
                                    </StyledTableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <StyledTableCell colSpan={4} style={{ textAlign: 'center' }}>
                                    Aucun établissement trouvé.
                                </StyledTableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </StyledTableContainer>
            <SGSTablePagination count={totalItems} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} />
        </Modal>
    );
};

export default ModalAjouterEtablissement;

const StyledTableContainer = styled(TableContainer)(() => ({
    boxShadow: 'none',
}));

const StyledHeaderCell = styled(TableCell)(({}) => ({
    '&.MuiTableCell-root': {
        color: '#FFF',
        fontFamily: 'Open Sans',
        fontStyle: 'normal',
        fontSize: '0.85rem',
        textAlign: 'center',
        verticalAlign: 'middle',
        paddingTop: '5px',
        height: '40px',
        paddingBottom: '3px',
        borderBottom: 'none',
        borderRight: '1px solid #3399DB',
        borderTop: '1px solid #3399DB',
        backgroundColor: '#3399DB',
        fontWeight: 700,
        '&:first-of-type': {
            borderLeft: '1px solid #3399DB',
        },
    },
}));

const StyledTableCell = styled(TableCell)(() => ({
    color: '#4F7793',
    fontFamily: 'Open Sans',
    fontStyle: 'normal',
    fontSize: '0.85rem',
    textAlign: 'center',
    verticalAlign: 'top',
    paddingTop: '5px',
    height: '30px',
    paddingBottom: '3px',
    borderBottom: 'none',
    borderRight: '1px solid #cdcdcd',
    borderTop: '1px solid #cdcdcd',
    '&:first-of-type': {
        borderLeft: '1px solid #cdcdcd',
    },
}));
